import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Input } from '@/components/ui';
import { AuthHeading, FormMessage } from './AuthLayout';
import { cognitoConfirmSignUp, cognitoResendCode, friendlyAuthError } from './cognito';

export default function ConfirmPage() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!email) return <Navigate to="/signin" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (code.trim().length < 4) return setError('Enter the code from your email.');
    setBusy(true);
    try {
      await cognitoConfirmSignUp(email, code);
      navigate('/signin', { replace: true, state: { email, confirmed: true } });
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    try {
      await cognitoResendCode(email);
      setNotice('A new code is on its way.');
      setCooldown(30);
    } catch (err) {
      setError(friendlyAuthError(err));
    }
  }

  return (
    <>
      <AuthHeading
        title="Check your email"
        subtitle={
          <>
            We sent a confirmation code to <strong className="font-medium text-ink">{email}</strong>.
          </>
        }
      />
      <FormMessage error={error} notice={notice} />
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input
          label="Confirmation code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
          placeholder="123456"
          className="text-center font-mono text-xl tracking-[0.35em]"
        />
        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Confirm
        </Button>
      </form>
      <div className="mt-6 flex items-center justify-between text-sm">
        <button type="button" onClick={resend} disabled={cooldown > 0} className="font-medium text-link hover:underline disabled:text-ink-muted disabled:no-underline">
          {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
        </button>
        <Link to="/signin" className="text-ink-muted hover:text-ink hover:underline">
          Back to sign in
        </Link>
      </div>
    </>
  );
}
